import { useState, useRef, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { MailDetailToolbar } from './MailDetailToolbar';
import {
  SkeletonBox,
  SkeletonText,
  CustomSelect,
  EmailChipInput,
} from '@/components';
import toast from 'react-hot-toast';
import DOMPurify from 'dompurify';

DOMPurify.addHook('afterSanitizeAttributes', function (node) {
  if ('target' in node) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

function extractNameFromEmail(raw = '') {
  if (!raw) return 'Unknown';
  const match = raw.match(/^(.*?)\s*<[^>]+>/);
  if (match) return match[1].trim().replace(/^["']|["']$/g, '');
  if (raw.includes('@')) return raw.split('@')[0];
  return raw;
}

function extractEmailOnly(raw = '') {
  if (!raw) return '';
  const match = raw.match(/<([^>]+)>/);
  if (match) return match[1];
  return raw;
}

function parseRecipientsList(raw) {
  if (!raw) return [];
  let items = [];
  if (Array.isArray(raw)) {
    items = raw;
  } else if (typeof raw === 'string') {
    // Split by comma outside of quotes or angle brackets
    items = raw.split(/,(?=(?:(?:[^<]*<[^>]*>)*[^<]*$))/);
  }
  return items
    .map((item) => {
      const trimmed = String(item).trim();
      if (!trimmed) return null;
      const email = extractEmailOnly(trimmed);
      const name = extractNameFromEmail(trimmed);
      const hasName =
        name &&
        name.toLowerCase() !== email.toLowerCase() &&
        name !== email.split('@')[0];
      return {
        raw: trimmed,
        name: hasName ? name : null,
        email: email || trimmed,
      };
    })
    .filter(Boolean);
}

function formatFullDate(raw) {
  if (!raw) return 'Mon, 28 Sept 2026, 15:06';
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return raw;
    return d.toLocaleString([], {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return raw;
  }
}

function formatGmailDate(raw) {
  if (!raw) return 'Oct 13, 2026, 2:44 PM';
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return raw;
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return raw;
  }
}

export function MailDetail({
  companyId,
  activeConversation,
  messages = [],
  isLoadingMessages = false,
  onBack,
  onSendMessage,
  onPrevEmail,
  onNextEmail,
  hasPrevEmail = false,
  hasNextEmail = false,
  currentIndex = -1,
  totalCount = 0,
  isLoadingNext = false,
  onDelete,
  onMarkUnread,
  onArchive,
}) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyMode, setReplyMode] = useState('reply'); // 'reply' | 'reply_all' | 'forward'
  const [forwardTo, setForwardTo] = useState('');
  const [toValue, setToValue] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [expandedThreads, setExpandedThreads] = useState({});
  const [openDetailMsgId, setOpenDetailMsgId] = useState(null);
  const detailsPopoverRef = useRef(null);

  useEffect(() => {
    if (!openDetailMsgId) return undefined;
    const handleDocClick = (e) => {
      if (
        detailsPopoverRef.current &&
        !detailsPopoverRef.current.contains(e.target)
      ) {
        setOpenDetailMsgId(null);
      }
    };
    document.addEventListener('mousedown', handleDocClick);
    return () => document.removeEventListener('mousedown', handleDocClick);
  }, [openDetailMsgId]);

  let myEmail = '';
  try {
    const userObj = JSON.parse(localStorage.getItem('user') || '{}');
    myEmail = userObj?.email || localStorage.getItem('user_email') || '';
  } catch {
    // Ignore JSON parse error
  }

  // Composer enhancements
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [ccValue, setCcValue] = useState('');
  const [bccValue, setBccValue] = useState('');
  const [attachedFile, setAttachedFile] = useState(null);

  const quillRef = useRef(null);

  useEffect(() => {
    if (showReplyForm && quillRef.current) {
      setTimeout(() => quillRef.current?.focus(), 100);
    }
  }, [showReplyForm]);

  if (!activeConversation) return null;

  const rawConv = activeConversation.raw || activeConversation;
  const senderName =
    activeConversation.client_name ||
    activeConversation.contactName ||
    activeConversation.name ||
    rawConv.client_name ||
    extractNameFromEmail(
      activeConversation.from_email || activeConversation.from || '',
    ) ||
    'RIDHAM TALAVIYA';

  const senderEmail =
    extractEmailOnly(
      activeConversation.from_email ||
        rawConv.from_email ||
        activeConversation.from ||
        rawConv.from ||
        'ridhamtalaviya.it.2216@gmail.com',
    ) || 'ridhamtalaviya.it.2216@gmail.com';

  const emailSubject =
    activeConversation.subject || rawConv.subject || 'Re: Eventaa Team';

  const allMessages = messages.flatMap((group) => group.messages || messages);
  const displayList =
    allMessages.length > 0
      ? allMessages
      : [
          {
            id: activeConversation.id,
            content:
              activeConversation.body ||
              rawConv.body ||
              activeConversation.snippet ||
              rawConv.snippet ||
              'Hello,\n\nThank you for reaching out to Aexon.\n\nRegards,\nSupport Team',
            is_html:
              activeConversation.is_html ||
              rawConv.is_html ||
              (activeConversation.body &&
                activeConversation.body.toLowerCase().includes('<html')) ||
              (rawConv.body && rawConv.body.toLowerCase().includes('<html')) ||
              false,
            from: senderEmail,
            timestamp: activeConversation.date || activeConversation.timestamp,
          },
        ];

  const toggleThreadCollapse = (index) => {
    setExpandedThreads((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleReplyClick = (mode = 'reply') => {
    setReplyMode(mode);
    setShowReplyForm(true);
  };

  const handleForwardClick = (msgToForward) => {
    setReplyMode('forward');
    setShowReplyForm(true);
    setForwardTo('');

    const targetMsg = msgToForward || displayList[displayList.length - 1] || {};
    const msgFrom = targetMsg.from_email
      ? `${targetMsg.from_name || ''} <${targetMsg.from_email}>`.trim()
      : targetMsg.from || senderEmail;
    const rawDate =
      targetMsg.timestamp ||
      targetMsg.date ||
      activeConversation.date ||
      activeConversation.timestamp;
    const formattedDateStr = rawDate
      ? new Date(rawDate).toLocaleString('en-US', {
          dateStyle: 'full',
          timeStyle: 'short',
        })
      : new Date().toLocaleString();
    const msgTo =
      activeConversation.to_email || rawConv.to_email || senderEmail;
    const bodyContent =
      targetMsg.content || targetMsg.body || targetMsg.snippet || '';

    const forwardBlock =
      `<br><br><div class="gmail_quote">---------- Forwarded message ---------<br>` +
      `<b>From:</b> ${msgFrom}<br>` +
      `<b>Date:</b> ${formattedDateStr}<br>` +
      `<b>Subject:</b> ${emailSubject}<br>` +
      `<b>To:</b> ${msgTo}<br><br>` +
      `${bodyContent}</div>`;

    setReplyBody(forwardBlock);
  };

  const handleSendReply = async (e) => {
    e?.preventDefault();
    const plainText = replyBody.replace(/<[^>]*>/g, '').trim();
    if (!plainText && !attachedFile) {
      toast.error('Please enter a message content.');
      return;
    }

    let targetTo =
      replyMode === 'forward' ? forwardTo.trim() : toValue || senderEmail;
    let subject = emailSubject;

    if (replyMode === 'forward') {
      if (!forwardTo.trim()) {
        toast.error('Please enter a recipient email address to forward to.');
        return;
      }
      targetTo = forwardTo.trim();
      subject = emailSubject.toLowerCase().startsWith('fwd:')
        ? emailSubject
        : `Fwd: ${emailSubject}`;
    } else {
      if (!targetTo) {
        toast.error('Please specify at least one recipient email address.');
        return;
      }
      subject = emailSubject.toLowerCase().startsWith('re:')
        ? emailSubject
        : `Re: ${emailSubject}`;
    }

    setIsSending(true);
    try {
      await onSendMessage?.(
        replyBody,
        attachedFile,
        subject,
        targetTo,
        ccValue,
        bccValue,
      );
      setReplyBody('');
      setForwardTo('');
      setAttachedFile(null);
      setShowReplyForm(false);
      toast.success(
        replyMode === 'forward'
          ? 'Email forwarded successfully!'
          : 'Email reply sent successfully!',
      );
    } catch {
      // Handled by parent
    } finally {
      setIsSending(false);
    }
  };

  const handleIframeLoad = (e) => {
    try {
      const iframe = e.target;
      if (
        iframe &&
        iframe.contentWindow &&
        iframe.contentWindow.document.body
      ) {
        const scrollHeight = iframe.contentWindow.document.body.scrollHeight;
        if (scrollHeight > 0) {
          iframe.style.height = `${scrollHeight + 20}px`;
        }
      }
    } catch {
      // Ignore cross-origin issues
    }
  };

  const senderInitial = (senderName || 'R').charAt(0).toUpperCase();

  return (
    <div className="flex flex-col h-full w-full bg-white font-sans overflow-hidden select-none">
      {/* Detail Toolbar */}
      <MailDetailToolbar
        onBack={onBack}
        onPrevEmail={onPrevEmail}
        onNextEmail={onNextEmail}
        hasPrevEmail={hasPrevEmail}
        hasNextEmail={hasNextEmail}
        currentIndex={currentIndex}
        totalCount={totalCount}
        isLoadingNext={isLoadingNext}
        onDelete={onDelete}
        onMarkUnread={onMarkUnread}
        onArchive={onArchive}
        companyId={companyId}
        threadId={activeConversation?.thread_id}
      />

      {/* Main Content Area - SINGLE Scrollbar */}
      <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-6 custom-scrollbar">
        {/* Subject Header & Badges */}
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 tracking-tight">
            {emailSubject}
          </h1>
          <span className="px-2.5 py-0.5 rounded text-xs font-bold bg-amber-400 text-slate-900 uppercase tracking-wide">
            External
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200">
            <span>Inbox</span>
            <button
              type="button"
              className="text-gray-400 hover:text-gray-600 ml-0.5"
            >
              ×
            </button>
          </span>
        </div>

        {/* Loading state */}
        {isLoadingMessages ? (
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-3">
              <SkeletonBox className="w-12 h-12 rounded-full" />
              <div className="space-y-2">
                <SkeletonText width="w-48" height="h-4" />
                <SkeletonText width="w-32" height="h-3" />
              </div>
            </div>
            <SkeletonText width="w-full" height="h-32" />
          </div>
        ) : (
          /* Render Thread Messages */
          <div className="space-y-6">
            {displayList.map((msg, idx) => {
              const isLatest = idx === displayList.length - 1;
              const isCollapsed = !isLatest && !expandedThreads[idx];
              const msgContent = msg.content || msg.body || msg.text || '';
              const msgFrom = msg.from || senderEmail;
              const msgFromName =
                msg.from_name ||
                msg.sender_name ||
                (msgFrom.includes('<') ? extractNameFromEmail(msgFrom) : '') ||
                senderName ||
                extractNameFromEmail(msgFrom);
              const msgFromEmail = extractEmailOnly(msgFrom) || senderEmail;
              const msgTime = formatFullDate(msg.timestamp || msg.date);

              if (isCollapsed) {
                return (
                  <div
                    key={msg.id || idx}
                    onClick={() => toggleThreadCollapse(idx)}
                    className="flex items-center justify-between p-3.5 bg-gray-50/80 hover:bg-gray-100 rounded-xl border border-gray-200/80 cursor-pointer transition-all"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-amber-500 text-white font-bold text-xs flex items-center justify-center shrink-0">
                        {msgFromName.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-xs font-bold text-gray-800 shrink-0">
                        {msgFromName}
                      </span>
                      <span className="text-xs text-gray-500 truncate">
                        –{' '}
                        {(msg.snippet || msgContent.replace(/<[^>]*>/g, ''))
                          .slice(0, 90)
                          .replace(/\n/g, ' ')}
                      </span>
                    </div>
                    <span className="text-xs text-gray-400 font-medium shrink-0 ml-2">
                      {msgTime}
                    </span>
                  </div>
                );
              }

              return (
                <div key={msg.id || idx} className="space-y-4">
                  {/* Sender Header Row */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3.5 min-w-0">
                      {/* Avatar */}
                      <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-amber-500 to-orange-500 text-white font-bold text-lg flex items-center justify-center shrink-0 shadow-sm">
                        {senderInitial}
                      </div>

                      {/* Sender Info */}
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-bold text-sm text-gray-900">
                            {msgFromName}
                          </span>
                          <span className="text-xs text-gray-500">
                            &lt;{msgFromEmail}&gt;
                          </span>
                        </div>
                        {(() => {
                          const msgKey = String(msg.id || idx);
                          const isDetailOpen = openDetailMsgId === msgKey;
                          const fromDomain = msgFromEmail.includes('@')
                            ? msgFromEmail.split('@')[1]
                            : 'domain.com';
                          const rawTo =
                            msg.to ||
                            msg.to_email ||
                            msg.recipients ||
                            msg.to_emails ||
                            activeConversation.to_email ||
                            activeConversation.to ||
                            rawConv.to_email ||
                            rawConv.to ||
                            myEmail ||
                            'me';
                          const recipientList = parseRecipientsList(rawTo);
                          const rawCc =
                            msg.cc ||
                            msg.cc_emails ||
                            activeConversation.cc ||
                            rawConv.cc;
                          const ccList = parseRecipientsList(rawCc);
                          const rawDate =
                            msg.timestamp ||
                            msg.date ||
                            activeConversation.date ||
                            activeConversation.timestamp;
                          const fullDateFormatted = formatGmailDate(rawDate);
                          const mailedByDomain = msg.mailed_by || fromDomain;
                          const signedByDomain = msg.signed_by || fromDomain;

                          return (
                            <div className="relative inline-block mt-0.5">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenDetailMsgId((prev) =>
                                    prev === msgKey ? null : msgKey,
                                  );
                                }}
                                className="inline-flex items-center gap-1 text-[12px] text-[#5f6368] hover:text-[#202124] py-0.5 px-1 -ml-1 rounded hover:bg-[#f1f3f4] transition-colors cursor-pointer select-none leading-none"
                                aria-label="Show email details"
                              >
                                <span>to me</span>
                                <svg
                                  className={`w-3 h-3 text-[#5f6368] transition-transform duration-150 ${isDetailOpen ? 'rotate-180' : ''}`}
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </button>

                              {/* Gmail-style Details Dropdown Popover */}
                              {isDetailOpen && (
                                <>
                                  {/* Invisible full-screen backdrop to handle outside clicks seamlessly across page & iframes */}
                                  <div
                                    className="fixed inset-0 z-40 bg-transparent"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenDetailMsgId(null);
                                    }}
                                  />
                                  <div
                                    ref={detailsPopoverRef}
                                    onClick={(e) => e.stopPropagation()}
                                    className="absolute left-0 top-full mt-1.5 z-50 bg-white border border-[#dadce0] rounded-[8px] shadow-[0_1px_3px_0_rgba(60,64,67,0.3),0_4px_8px_3px_rgba(60,64,67,0.15)] p-4 w-max min-w-[360px] max-w-[min(580px,calc(100vw-48px))] text-[12px] font-sans text-[#202124] animate-fadeIn"
                                  >
                                    <table className="w-full text-left border-collapse">
                                      <tbody className="divide-y divide-transparent">
                                        <tr className="align-top">
                                          <td className="w-[74px] text-right text-[#5f6368] pr-3 py-1 font-normal whitespace-nowrap select-none">
                                            from:
                                          </td>
                                          <td className="py-1 text-[#202124] leading-[20px]">
                                            <span className="font-semibold text-[#202124]">
                                              {(msgFromName || '')
                                                .replace(/<[^>]+>/g, '')
                                                .trim() ||
                                                msgFromEmail.split('@')[0]}
                                            </span>{' '}
                                            <span className="text-[#5f6368] whitespace-nowrap">
                                              &lt;{msgFromEmail}&gt;
                                            </span>
                                          </td>
                                        </tr>

                                        <tr className="align-top">
                                          <td className="w-[74px] text-right text-[#5f6368] pr-3 py-1 font-normal whitespace-nowrap select-none">
                                            to:
                                          </td>
                                          <td className="py-1 text-[#202124] leading-[20px]">
                                            {recipientList.length === 0 ? (
                                              <span className="text-[#202124]">
                                                {myEmail || 'me'}
                                              </span>
                                            ) : (
                                              <div className="flex flex-col gap-0.5">
                                                {recipientList.map((rec, i) => (
                                                  <div
                                                    key={i}
                                                    className="leading-[18px]"
                                                  >
                                                    {rec.name ? (
                                                      <>
                                                        <span className="font-semibold text-[#202124]">
                                                          {rec.name}
                                                        </span>{' '}
                                                        <span className="text-[#5f6368] whitespace-nowrap">
                                                          &lt;{rec.email}&gt;
                                                        </span>
                                                      </>
                                                    ) : (
                                                      <span className="text-[#202124] whitespace-nowrap">
                                                        {rec.email}
                                                      </span>
                                                    )}
                                                    {i <
                                                      recipientList.length -
                                                        1 && (
                                                      <span className="text-[#5f6368]">
                                                        ,
                                                      </span>
                                                    )}
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </td>
                                        </tr>

                                        {ccList.length > 0 && (
                                          <tr className="align-top">
                                            <td className="w-[74px] text-right text-[#5f6368] pr-3 py-1 font-normal whitespace-nowrap select-none">
                                              cc:
                                            </td>
                                            <td className="py-1 text-[#202124] leading-[20px]">
                                              <div className="flex flex-col gap-0.5">
                                                {ccList.map((rec, i) => (
                                                  <div
                                                    key={i}
                                                    className="leading-[18px]"
                                                  >
                                                    {rec.name ? (
                                                      <>
                                                        <span className="font-semibold text-[#202124]">
                                                          {rec.name}
                                                        </span>{' '}
                                                        <span className="text-[#5f6368] whitespace-nowrap">
                                                          &lt;{rec.email}&gt;
                                                        </span>
                                                      </>
                                                    ) : (
                                                      <span className="text-[#202124] whitespace-nowrap">
                                                        {rec.email}
                                                      </span>
                                                    )}
                                                    {i < ccList.length - 1 && (
                                                      <span className="text-[#5f6368]">
                                                        ,
                                                      </span>
                                                    )}
                                                  </div>
                                                ))}
                                              </div>
                                            </td>
                                          </tr>
                                        )}

                                        <tr className="align-top">
                                          <td className="w-[74px] text-right text-[#5f6368] pr-3 py-1 font-normal whitespace-nowrap select-none">
                                            date:
                                          </td>
                                          <td className="py-1 text-[#202124] leading-[20px] whitespace-nowrap">
                                            {fullDateFormatted}
                                          </td>
                                        </tr>

                                        <tr className="align-top">
                                          <td className="w-[74px] text-right text-[#5f6368] pr-3 py-1 font-normal whitespace-nowrap select-none">
                                            subject:
                                          </td>
                                          <td className="py-1 text-[#202124] leading-[20px] break-words">
                                            {emailSubject}
                                          </td>
                                        </tr>

                                        <tr className="align-top">
                                          <td className="w-[74px] text-right text-[#5f6368] pr-3 py-1 font-normal whitespace-nowrap select-none">
                                            mailed-by:
                                          </td>
                                          <td className="py-1 text-[#202124] leading-[20px] whitespace-nowrap">
                                            {mailedByDomain}
                                          </td>
                                        </tr>

                                        <tr className="align-top">
                                          <td className="w-[74px] text-right text-[#5f6368] pr-3 py-1 font-normal whitespace-nowrap select-none">
                                            signed-by:
                                          </td>
                                          <td className="py-1 text-[#202124] leading-[20px] whitespace-nowrap">
                                            {signedByDomain}
                                          </td>
                                        </tr>

                                        <tr className="align-top">
                                          <td className="w-[74px] text-right text-[#5f6368] pr-3 py-1 font-normal whitespace-nowrap select-none">
                                            security:
                                          </td>
                                          <td className="py-1 text-[#202124] leading-[18px]">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                              <svg
                                                className="w-3.5 h-3.5 text-[#5f6368] shrink-0"
                                                viewBox="0 0 24 24"
                                                fill="currentColor"
                                              >
                                                <path
                                                  fillRule="evenodd"
                                                  d="M12 1.5a5.25 5.25 0 00-5.25 5.25v3a3 3 0 00-3 3v6.75a3 3 0 003 3h10.5a3 3 0 003-3v-6.75a3 3 0 00-3-3v-3c0-2.9-2.35-5.25-5.25-5.25zm3.75 8.25v-3a3.75 3.75 0 00-7.5 0v3h7.5z"
                                                  clipRule="evenodd"
                                                />
                                              </svg>
                                              <span>
                                                Standard encryption (TLS)
                                              </span>
                                              <a
                                                href="https://support.google.com/mail/answer/6330403"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-[#1a73e8] hover:underline cursor-pointer"
                                              >
                                                Learn more
                                              </a>
                                            </div>
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Right Info & Actions */}
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs font-medium text-gray-500 hidden sm:inline">
                        {msgTime}
                      </span>

                      {/* Reply Arrow Icon */}
                      <button
                        type="button"
                        onClick={() => setShowReplyForm(true)}
                        className="p-1.5 text-gray-500 hover:text-gray-800 rounded-lg hover:bg-gray-100 transition-colors"
                        title="Reply"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Clean Email Body (Dynamic Height iframe - no inner scrollbar) */}
                  {msg.is_html ||
                  Boolean(
                    msgContent &&
                    (/<[a-z][\s\S]*>/i.test(msgContent) ||
                      msgContent.includes('<p>') ||
                      msgContent.includes('<p ') ||
                      msgContent.includes('<br') ||
                      msgContent.includes('<div>') ||
                      msgContent.includes('<div ') ||
                      msgContent.includes('</span>') ||
                      msgContent.includes('class=')),
                  ) ? (
                    <div className="w-full border border-gray-200 rounded-xl overflow-hidden bg-white mt-4 shadow-sm">
                      <iframe
                        srcDoc={msgContent}
                        onLoad={handleIframeLoad}
                        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                        title="email-body"
                        className="w-full min-h-[300px] border-0"
                      />
                    </div>
                  ) : (
                    <div className="text-sm text-gray-800 leading-relaxed whitespace-pre-line font-sans pt-2">
                      {msgContent}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Bottom Action Pill Buttons (when reply form is closed) */}
        {!showReplyForm && (
          <div className="pt-6 flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => handleReplyClick('reply')}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-border-default bg-white hover:bg-surface-mist hover:border-primary/40 text-xs font-semibold text-text-primary shadow-2xs transition-all cursor-pointer"
            >
              <svg
                className="w-3.5 h-3.5 text-text-secondary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                />
              </svg>
              <span>Reply</span>
            </button>

            <button
              type="button"
              onClick={() => handleReplyClick('reply_all')}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-border-default bg-white hover:bg-surface-mist hover:border-primary/40 text-xs font-semibold text-text-primary shadow-2xs transition-all cursor-pointer"
            >
              <svg
                className="w-3.5 h-3.5 text-text-secondary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                />
              </svg>
              <span>Reply All</span>
            </button>

            <button
              type="button"
              onClick={() => handleForwardClick()}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-border-default bg-white hover:bg-surface-mist hover:border-primary/40 text-xs font-semibold text-text-primary shadow-2xs transition-all cursor-pointer"
            >
              <svg
                className="w-3.5 h-3.5 text-text-secondary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6"
                />
              </svg>
              <span>Forward</span>
            </button>
          </div>
        )}

        {/* Themed Inline Reply Form */}
        {showReplyForm && (
          <div className="mt-6 flex items-start w-full">
            {/* Main composer box */}
            <form
              onSubmit={handleSendReply}
              className="flex-1 border border-border-default rounded-2xl bg-white shadow-sm hover:shadow-md transition-shadow flex flex-col overflow-hidden"
            >
              {/* Header with To / Cc / Bcc */}
              <div className="px-3 sm:px-4 py-2 rounded-t-2xl border-b border-border-default flex items-start justify-between text-xs text-text-primary bg-surface-mist/40 gap-2 min-h-[44px]">
                <div className="flex items-start gap-2 flex-1 min-w-0 flex-wrap sm:flex-nowrap">
                  <div className="shrink-0 pt-0.5">
                    <CustomSelect
                      value={replyMode}
                      onChange={(val) => {
                        if (val === 'forward') handleForwardClick();
                        else handleReplyClick(val);
                      }}
                      options={[
                        { value: 'reply', label: 'Reply' },
                        { value: 'reply_all', label: 'Reply All' },
                        { value: 'forward', label: 'Forward' },
                      ]}
                      buttonClassName="flex items-center justify-between gap-1.5 h-7 px-2.5 rounded-lg bg-white border border-border-default text-xs font-medium text-text-primary hover:border-primary/50 transition-colors cursor-pointer shrink-0 min-w-[84px] shadow-2xs"
                      dropdownWidth={130}
                      textSizeClass="text-xs font-medium"
                    />
                  </div>

                  {replyMode === 'forward' ? (
                    <div className="flex items-start gap-1.5 flex-1 min-w-0 pt-0.5">
                      <span className="font-medium text-text-secondary text-xs shrink-0 h-7 flex items-center select-none">
                        To
                      </span>
                      <div className="flex-1 min-w-0">
                        <EmailChipInput
                          value={forwardTo}
                          onChange={setForwardTo}
                          placeholder="Recipient email address..."
                          autoFocus
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-1.5 flex-1 min-w-0 pt-0.5">
                      <span className="font-medium text-text-secondary text-xs shrink-0 h-7 flex items-center select-none">
                        To
                      </span>
                      <div className="flex-1 min-w-0">
                        <EmailChipInput
                          value={toValue || senderEmail}
                          onChange={setToValue}
                          placeholder="Recipient email address..."
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-1 font-medium shrink-0 pt-0.5">
                  <button
                    type="button"
                    onClick={() => setShowCc(!showCc)}
                    className={`h-7 px-2.5 flex items-center justify-center rounded-lg text-xs font-medium transition-all cursor-pointer ${
                      showCc
                        ? 'bg-primary/15 text-primary font-bold shadow-2xs'
                        : 'text-text-secondary hover:text-text-primary hover:bg-white'
                    }`}
                  >
                    Cc
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowBcc(!showBcc)}
                    className={`h-7 px-2.5 flex items-center justify-center rounded-lg text-xs font-medium transition-all cursor-pointer ${
                      showBcc
                        ? 'bg-primary/15 text-primary font-bold shadow-2xs'
                        : 'text-text-secondary hover:text-text-primary hover:bg-white'
                    }`}
                  >
                    Bcc
                  </button>
                </div>
              </div>

              {/* Optional CC Field */}
              {showCc && (
                <div className="px-3 sm:px-4 py-1.5 border-b border-border-default bg-surface-mist/20 flex items-start gap-2 text-xs min-h-[40px]">
                  <span className="font-medium text-text-secondary w-8 shrink-0 h-7 flex items-center select-none pt-0.5">
                    Cc
                  </span>
                  <div className="flex-1 min-w-0">
                    <EmailChipInput
                      value={ccValue}
                      onChange={setCcValue}
                      placeholder="Cc recipients..."
                    />
                  </div>
                </div>
              )}

              {/* Optional BCC Field */}
              {showBcc && (
                <div className="px-3 sm:px-4 py-1.5 border-b border-border-default bg-surface-mist/20 flex items-start gap-2 text-xs min-h-[40px]">
                  <span className="font-medium text-text-secondary w-8 shrink-0 h-7 flex items-center select-none pt-0.5">
                    Bcc
                  </span>
                  <div className="flex-1 min-w-0">
                    <EmailChipInput
                      value={bccValue}
                      onChange={setBccValue}
                      placeholder="Bcc recipients..."
                    />
                  </div>
                </div>
              )}
              {/* Attached File Preview */}
              {attachedFile && (
                <div className="px-3 sm:px-4 py-2 border-b border-border-default bg-surface-mint/40 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs text-text-primary">
                    <svg
                      className="w-4 h-4 text-primary"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                      />
                    </svg>
                    <span className="font-medium truncate max-w-[200px] sm:max-w-xs text-text-primary">
                      {attachedFile.name}
                    </span>
                    <span className="text-text-secondary">
                      ({(attachedFile.size / 1024).toFixed(1)} KB)
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAttachedFile(null)}
                    className="p-1 hover:bg-surface-mint rounded-full text-text-secondary hover:text-status-error transition-colors"
                    title="Remove attachment"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              )}

              {/* ReactQuill Editor Area */}
              <div className="relative">
                <style>{`
                  .mail-reply-quill {
                    display: flex;
                    flex-direction: column;
                    border: none !important;
                  }
                  .mail-reply-quill .ql-toolbar.ql-snow {
                    border: none !important;
                    border-bottom: 1px solid #E5E5E5 !important;
                    background-color: #F8FAF8;
                    padding: 6px 12px;
                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                    gap: 1px;
                  }
                  .mail-reply-quill .ql-toolbar.ql-snow button {
                    border-radius: 6px !important;
                    width: 26px !important;
                    height: 26px !important;
                    margin: 1px !important;
                    transition: all 0.15s ease !important;
                    display: inline-flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                  }
                  .mail-reply-quill .ql-toolbar.ql-snow button:hover {
                    background-color: #EBF4EA !important;
                  }
                  .mail-reply-quill .ql-toolbar.ql-snow button.ql-active {
                    background-color: #EBF4EA !important;
                  }
                  .mail-reply-quill .ql-toolbar.ql-snow .ql-picker {
                    height: 26px !important;
                    margin: 1px !important;
                    border-radius: 6px !important;
                  }
                  .mail-reply-quill .ql-toolbar.ql-snow .ql-picker:hover {
                    background-color: #EBF4EA !important;
                  }
                  .mail-reply-quill .ql-toolbar.ql-snow button:hover,
                  .mail-reply-quill .ql-toolbar.ql-snow button:focus,
                  .mail-reply-quill .ql-toolbar.ql-snow button.ql-active,
                  .mail-reply-quill .ql-toolbar.ql-snow .ql-picker-label:hover,
                  .mail-reply-quill .ql-toolbar.ql-snow .ql-picker-label.ql-active,
                  .mail-reply-quill .ql-toolbar.ql-snow .ql-picker-item:hover,
                  .mail-reply-quill .ql-toolbar.ql-snow .ql-picker-item.ql-selected {
                    color: #50AB3D !important;
                  }
                  .mail-reply-quill .ql-toolbar.ql-snow button:hover .ql-stroke,
                  .mail-reply-quill .ql-toolbar.ql-snow button:focus .ql-stroke,
                  .mail-reply-quill .ql-toolbar.ql-snow button.ql-active .ql-stroke,
                  .mail-reply-quill .ql-toolbar.ql-snow .ql-picker-label:hover .ql-stroke,
                  .mail-reply-quill .ql-toolbar.ql-snow .ql-picker-label.ql-active .ql-stroke,
                  .mail-reply-quill .ql-toolbar.ql-snow .ql-picker-item:hover .ql-stroke,
                  .mail-reply-quill .ql-toolbar.ql-snow .ql-picker-item.ql-selected .ql-stroke {
                    stroke: #50AB3D !important;
                  }
                  .mail-reply-quill .ql-toolbar.ql-snow button:hover .ql-fill,
                  .mail-reply-quill .ql-toolbar.ql-snow button:focus .ql-fill,
                  .mail-reply-quill .ql-toolbar.ql-snow button.ql-active .ql-fill,
                  .mail-reply-quill .ql-toolbar.ql-snow .ql-picker-label:hover .ql-fill,
                  .mail-reply-quill .ql-toolbar.ql-snow .ql-picker-label.ql-active .ql-fill,
                  .mail-reply-quill .ql-toolbar.ql-snow .ql-picker-item:hover .ql-fill,
                  .mail-reply-quill .ql-toolbar.ql-snow .ql-picker-item.ql-selected .ql-fill {
                    fill: #50AB3D !important;
                  }
                  .mail-reply-quill .ql-picker.ql-header {
                    width: 96px !important;
                  }
                  .mail-reply-quill .ql-picker.ql-header .ql-picker-label {
                    font-size: 12px !important;
                    font-weight: 500 !important;
                    padding: 2px 6px !important;
                  }
                  .mail-reply-quill .ql-picker.ql-header .ql-picker-item {
                    font-size: 13px !important;
                    font-weight: 500 !important;
                    padding: 6px 10px !important;
                    line-height: 1.4 !important;
                  }
                  .mail-reply-quill .ql-picker.ql-header .ql-picker-item::before {
                    font-size: 13px !important;
                    font-weight: 500 !important;
                  }
                  .mail-reply-quill .ql-picker.ql-header .ql-picker-item[data-value="1"]::before {
                    font-size: 14px !important;
                    font-weight: 600 !important;
                  }
                  .mail-reply-quill .ql-picker.ql-header .ql-picker-item[data-value="2"]::before {
                    font-size: 13px !important;
                    font-weight: 600 !important;
                  }
                  .mail-reply-quill .ql-picker.ql-header .ql-picker-item[data-value="3"]::before {
                    font-size: 12px !important;
                    font-weight: 600 !important;
                  }
                  .mail-reply-quill .ql-container.ql-snow {
                    border: none !important;
                    font-family: inherit;
                  }
                  .mail-reply-quill .ql-editor {
                    min-height: 140px;
                    max-height: 320px;
                    padding: 12px 16px;
                    font-size: 14px;
                    line-height: 1.6;
                    color: #1F2241;
                  }
                  .mail-reply-quill .ql-editor.ql-blank::before {
                    font-style: normal;
                    color: #868686;
                    left: 16px;
                    right: 16px;
                  }
                  .mail-reply-quill .ql-picker-options {
                    z-index: 100 !important;
                    border-radius: 10px !important;
                    box-shadow: 0 4px 16px -2px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0,0,0,0.06) !important;
                    border: 1px solid #E5E5E5 !important;
                    background-color: #FFFFFF !important;
                    padding: 4px !important;
                  }
                  .mail-reply-quill .ql-picker-item {
                    border-radius: 6px !important;
                    transition: all 0.15s ease !important;
                  }
                  .mail-reply-quill .ql-picker-item:hover {
                    background-color: #F3F5F3 !important;
                  }
                  .mail-reply-quill .ql-snow .ql-tooltip {
                    position: absolute !important;
                    z-index: 100 !important;
                    background-color: #FFFFFF !important;
                    border: 1px solid #E5E5E5 !important;
                    border-radius: 8px !important;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
                    padding: 6px 10px !important;
                  }
                `}</style>
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={replyBody}
                  onChange={setReplyBody}
                  modules={{
                    toolbar: [
                      [{ header: [1, 2, 3, false] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ color: [] }, { background: [] }],
                      [{ list: 'ordered' }, { list: 'bullet' }, { align: [] }],
                      ['link', 'image', 'blockquote', 'code-block'],
                      ['clean'],
                    ],
                  }}
                  placeholder="Write your email reply..."
                  className="mail-reply-quill"
                />
              </div>

              {/* Bottom Action Bar */}
              <div className="px-4 py-2.5 rounded-b-2xl bg-white flex items-center justify-between gap-3">
                {/* Left controls */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  {/* Send Button */}
                  <button
                    type="submit"
                    disabled={
                      isSending ||
                      (!replyBody.replace(/<[^>]*>/g, '').trim() &&
                        !attachedFile)
                    }
                    className="px-5 py-2 text-xs font-semibold rounded-xl bg-primary hover:bg-primary-hover shadow-sm hover:shadow text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 active:scale-95 cursor-pointer"
                  >
                    {isSending ? (
                      <>
                        <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                          />
                        </svg>
                        <span>Send</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Right controls: Trash / Discard Draft */}
                <button
                  type="button"
                  onClick={() => {
                    setShowReplyForm(false);
                    setReplyBody('');
                    setAttachedFile(null);
                  }}
                  className="p-2 text-text-secondary hover:text-status-error rounded-xl hover:bg-red-50 transition-colors cursor-pointer"
                  title="Discard draft"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
